import React, { useState, useEffect, useRef } from "react";
import styles from "./styles.module.css";
import Text from "@Components/Text";
import PropTypes from "prop-types";
import { Icon } from "semantic-ui-react";

const getIconForField = (text) => {
	const lower = (text || "").toLowerCase();
	if (lower.includes("etiqueta") || lower.includes("tag")) return "tag";
	if (lower.includes("fecha") || lower.includes("date")) return "calendar outline";
	if (lower.includes("id")) return "hashtag";
	return "circle outline";
};

const DropdownOrder = ({
	orderByOptions = [],
	orderByValue,
	onOrderByChange,
	orderDirectionOptions = [],
	orderDirectionValue,
	onOrderDirectionChange,
	error,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef(null);

	let selectedText = "Seleccionar";
	let selectedIcon = "circle outline";
	if (orderByValue) {
		const foundOrder = orderByOptions.find((o) => {
			return o.value === orderByValue;
		});
		if (foundOrder) {
			selectedText = foundOrder.text;
			selectedIcon = getIconForField(foundOrder.text);
		}
	}

	const currentDirectionText = orderDirectionValue
		? orderDirectionOptions.find((d) => {
				return d.value === orderDirectionValue;
		  })?.text || ""
		: "Seleccionar orden";

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			return document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	const handleSelect = (orderBy) => {
		onOrderByChange(orderBy);
		// If no direction is selected yet, default to the first one
		if (!orderDirectionValue && orderDirectionOptions.length > 0) {
			onOrderDirectionChange(orderDirectionOptions[0].value);
		}
		setIsOpen(false);
	};

	const handleClear = (e) => {
		e.stopPropagation();
		onOrderByChange("");
		onOrderDirectionChange("");
		setIsOpen(false);
	};

	const toggleDirection = (e) => {
		e.preventDefault();
		if (!orderDirectionOptions || orderDirectionOptions.length === 0) return;

		const currentIndex = orderDirectionOptions.findIndex((d) => {
			return d.value === orderDirectionValue;
		});
		// Toggle between 0 and 1, or default to 0 if none selected
		const nextIndex = currentIndex === 0 ? 1 : 0;
		if (orderDirectionOptions[nextIndex]) {
			onOrderDirectionChange(orderDirectionOptions[nextIndex].value);
		}
	};

	// Determine arrow icon (up for asc, down for desc, defaults to up)
	const isAscending = orderDirectionValue === orderDirectionOptions[0]?.value;
	const directionIconName = isAscending ? "sort amount up" : "sort amount down";

	return (
		<div className={styles.dropdownOrderContainer} ref={dropdownRef}>
			<Text className={styles.label}>Ordenar por:</Text>

			<div className={styles.inputGroup}>
				<div
					role="button"
					tabIndex={0}
					className={`${styles.trigger} ${isOpen ? styles.triggerOpen : ""} ${error ? styles.triggerError : ""}`}
					onClick={() => {
						return setIsOpen(!isOpen);
					}}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							return setIsOpen(!isOpen);
						}
					}}>
					<div className={styles.triggerContent}>
						<Icon name={selectedIcon} className={styles.triggerIcon} />
						<span className={styles.triggerText}>{selectedText}</span>
					</div>
					{orderByValue ? (
						<Icon name="close" className={styles.clearIcon} onClick={handleClear} title="Limpiar" />
					) : (
						<Icon name="angle down" className={styles.chevronIcon} />
					)}
				</div>

				<button
					type="button"
					className={`${styles.directionBtn} ${error && !orderDirectionValue ? styles.directionBtnError : ""}`}
					onClick={toggleDirection}
					title={currentDirectionText}
					disabled={!orderByValue}>
					<Icon name={directionIconName} className={styles.directionIcon} />
					{orderDirectionValue && <span className={styles.directionLabel}>{isAscending ? "Asc" : "Desc"}</span>}
				</button>
			</div>

			{isOpen && (
				<div className={styles.menu}>
					<div className={styles.menuHeader}>CAMPO A ORDENAR</div>
					{orderByOptions.map((opt) => {
						const isSelected = opt.value === orderByValue;
						return (
							<div
								key={opt.key || opt.value}
								role="button"
								tabIndex={0}
								className={`${styles.option} ${isSelected ? styles.optionSelected : ""}`}
								onClick={() => {
									return handleSelect(opt.value);
								}}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										return handleSelect(opt.value);
									}
								}}>
								<Icon name={getIconForField(opt.text)} className={styles.optionIcon} />
								<div className={styles.optionText}>
									<span className={styles.optionMainText}>{opt.text}</span>
								</div>
								{isSelected && <Icon name="check" className={styles.checkIcon} />}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

DropdownOrder.propTypes = {
	orderByOptions: PropTypes.array,
	orderByValue: PropTypes.string,
	onOrderByChange: PropTypes.func.isRequired,
	orderDirectionOptions: PropTypes.array,
	orderDirectionValue: PropTypes.string,
	onOrderDirectionChange: PropTypes.func.isRequired,
	error: PropTypes.bool,
};

export default DropdownOrder;
